import { FC, memo, useMemo } from "react";
import { format } from "date-fns";
import { Badge, Box, Tooltip, useTheme } from "@mui/material";
import {
  DateRangePickerDay,
  DateRangePickerDayProps,
} from "@mui/x-date-pickers-pro";
import __ from "src/utils/Translations";
import findInAbsenceRequestDays from "src/utils/Company/Absences/findInAbsenceRequestDays";
import isDayExcluded from "src/utils/Company/Absences/isDayExcluded";
import { IAbsenceRequestDay } from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";
import { IExcludedDay } from "src/Pages/Company/Absences/Calendars/Calendars.models";
import isOutsideSubmitLimitation from "src/utils/Company/Absences/isOutsideSubmitLimitation";
import SubmitLimitationTooltip, {
  SUBMIT_LIMITATION_TOOLTIP_KEY,
} from "./SubmitLimitationTooltip";
import {
  getAbsenceRequestDayStyles,
  getPickerStyles,
  getRangeIntervalStyles,
} from "./Datepicker.styles";

interface ICustomDateRangePickersDayProps extends DateRangePickerDayProps {
  absenceRequestDays: IAbsenceRequestDay[];
  excludedDays: IExcludedDay[];
  plannedDays: string[];
  multipleRequestsPerDayAllowed: boolean;
  billInCalendarDays: boolean;
  submitLimitationFrom: string; // ISO date in format YYYY-MM-DD
  submitLimitationTo: string; // ISO date in format YYYY-MM-DD
}

const CustomDateRangePickersDay: FC<ICustomDateRangePickersDayProps> = ({
  absenceRequestDays = [],
  excludedDays = [],
  plannedDays = [],
  multipleRequestsPerDayAllowed,
  billInCalendarDays,
  submitLimitationFrom,
  submitLimitationTo,
  ...props
}) => {
  const theme = useTheme();
  const { day, outsideCurrentMonth, isHighlighting } = props;
  const parsedToISO = format(new Date(day), "yyyy-MM-dd");
  const requestDay = findInAbsenceRequestDays(day, absenceRequestDays);
  const isRequestDay = !!requestDay && requestDay.absenceName;
  // Normal excluded days (i.e. weekends or holidays)
  const isExcluded = isDayExcluded(day, excludedDays);
  // Excluded days on absence types counting excluded days to balance
  const isCustomExcluded = billInCalendarDays && isExcluded;
  const plannedDaysSet = useMemo(() => new Set(plannedDays), [plannedDays]);
  const isPlannedDay = plannedDaysSet.has(parsedToISO);
  const isLimitationDisabled = isOutsideSubmitLimitation(
    day,
    submitLimitationFrom,
    submitLimitationTo
  );
  const allowsMultipleRequestsPerDay =
    !multipleRequestsPerDayAllowed ||
    !requestDay?.multipleRequestsPerDayAllowed;

  const pickerStyles =
    isRequestDay && !isHighlighting
      ? getPickerStyles(true, requestDay?.bgColor, requestDay?.position)
      : getRangeIntervalStyles(theme, isExcluded, isCustomExcluded);

  const dayContent =
    isRequestDay && !outsideCurrentMonth && !isHighlighting ? (
      <Box component="span" sx={getAbsenceRequestDayStyles(requestDay?.color)}>
        {format(day, "d")}
      </Box>
    ) : // eslint-disable-next-line no-undefined
    undefined;

  const isDisabled =
    isLimitationDisabled ||
    (isRequestDay && !outsideCurrentMonth && allowsMultipleRequestsPerDay);

  if (isRequestDay && !outsideCurrentMonth && !isHighlighting) {
    const dayElement = (
      <Tooltip
        title={
          isLimitationDisabled
            ? __(SUBMIT_LIMITATION_TOOLTIP_KEY)
            : requestDay.absenceName
        }
        placement="top"
        arrow
        slotProps={{
          popper: {
            modifiers: [
              {
                name: "preventOverflow",
                options: {
                  altAxis: true,
                  tether: false,
                },
              },
            ],
          },
        }}
      >
        <Box
          component="span"
          sx={{
            display: "inline-block",
            lineHeight: 0,
          }}
        >
          <DateRangePickerDay
            {...props}
            disabled={!!isDisabled}
            sx={pickerStyles}
            data-testid="absence-datepicker-request-day"
          >
            {dayContent}
          </DateRangePickerDay>
        </Box>
      </Tooltip>
    );

    return isPlannedDay ? (
      <Badge badgeContent="" variant="dot" color="primary" overlap="circular">
        {dayElement}
      </Badge>
    ) : (
      dayElement
    );
  }

  const dayElement = (
    <DateRangePickerDay
      {...props}
      disabled={!!isDisabled}
      sx={pickerStyles}
      data-testid="absence-datepicker-day"
    />
  );

  const wrappedDayElement = isLimitationDisabled ? (
    <SubmitLimitationTooltip>{dayElement}</SubmitLimitationTooltip>
  ) : (
    dayElement
  );

  return isPlannedDay && !outsideCurrentMonth && !isHighlighting ? (
    <Badge badgeContent="" variant="dot" color="primary" overlap="circular">
      {wrappedDayElement}
    </Badge>
  ) : (
    wrappedDayElement
  );
};

export default memo(CustomDateRangePickersDay);
